import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReviewSlideshow from '../components/ReviewSlideshow';
import { fetchCollection } from '../lib/cms';

const About = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchCollection('testimonials').then(setTestimonials);
  }, []);

  return (
    <div className="bg-white">
      <Helmet>
        <title>Salonen — Toftes Hundesalon</title>
        <meta name="description" content="Læs om Helene og salonen i Tranbjerg. Vi har fokus på tryghed og professionel pleje af din hund." />
        <meta property="og:title" content="Salonen — Toftes Hundesalon" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col-reverse lg:flex-row gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-full lg:w-1/2"
            >
              <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Om salonen</h2>
              <h1 className="!text-[2rem] md:text-6xl font-serif italic text-stone-900 mb-10 leading-tight">
                Velkommen til <br /> Toftes Hundesalon
              </h1>
              <div className="prose prose-lg text-gray-600 space-y-6 leading-relaxed">
                <p>
                  Siden jeg kan huske, har jeg elsket hunde, og da jeg blev 20, fik jeg min første hund – en fantastisk Shih Tzu, Cæsar. Jeg savner ham stadig den dag i dag.
                </p>
                <p>
                  Han var med til at åbne mine øjne for faget, idet en hund skal klippes og plejes for at trives. Min passion for både grooming og hunde smeltede sammen og skabte grobund for min drøm – at blive selvstændig hundefrisør.
                </p>
                <p>
                  Jeg har fulgt denne drøm siden 2009, og jeg er stolt over at kunne byde jer velkommen i min salon på Christian x’s vej 264 i Slet, kun 15 minutter fra Aarhus midtby.
                </p>
                <p>
                  Jeg er mor til to fantastiske små piger, Emmelie og Penelope, som også deler min kærlighed til hunde. I vores hjem, hvor salonen ligger i kælderen, bor også den milde og rolige Shih Tzu, Philine. Hende kan I også hilse på, når I besøger mig – hun er med på jobbet hver dag.
                </p>
                <p>
                  For at fortælle lidt mere om min baggrund: Jeg er vokset op med en far, som er hundetræner, gik vagt og deltog i konkurrencer med den sejeste schæfer, Anni. Hun var ikke en almindelig selskabshund, men havde en stærk karakter og var en stor del af familien og min opvækst.
                </p>
                <p>
                  At omgås og forstå hunde ligger i min DNA, og det har givet mig en naturlig ro og tarv. Alle hunde er forskellige, og jeg forstår bredden i hundens natur. Hunde i alle afstøbninger har altid været som en magnet på mig – de er og bliver en helt uundværlig del af mit liv.
                </p>
                <p>
                  Jeg håber, min hjemmeside har vakt din interesse. Jeg glæder mig til at byde dig og din hund velkommen i salonen.
                </p>
                <p>
                  Jeg står altid til rådighed med råd og vejledning omkring pelspleje og kan frit kontaktes med alle former for spørgsmål. Jeg svarer hurtigt på både sms og mail.
                </p>
                <p className="font-serif italic text-stone-900 pt-4">
                  – Helene
                </p>
              </div>
              <div className="mt-12">
                <a 
                  href="https://tofteshundesalon.planway.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-stone-800 text-white px-12 py-4 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all inline-block"
                >
                  Book din tid her
                </a>
              </div>
            </motion.div>
            <div className="w-full lg:w-1/2 lg:sticky lg:top-24">
              <img 
                src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/dc5ca2fa-1e97-4f0f-9207-eba46cf6c49d.jpg" 
                alt="Helene og hund" 
                className="w-full h-auto rounded-none shadow-lg block"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Location Section */}
      <section className="py-32 px-4 bg-stone-100 text-stone-900 relative overflow-hidden border-t border-stone-200">
        <div className="absolute top-0 left-0 w-1/4 h-full bg-stone-200/50 skew-x-12 -translate-x-1/2" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-sm uppercase tracking-[0.6em] font-bold mb-8 text-stone-600">Find os</h2>
              <h3 className="!text-[1.6rem] md:text-6xl font-serif italic mb-10 leading-tight text-stone-900">
                Her finder du <br /> salonen
              </h3>
              <p className="text-xl text-stone-700 mb-12 leading-relaxed italic">
                Hundesalonen finder du på Christian x’s vej 264, 8310 Tranbjerg J, Slet.
              </p>
              <a 
                href="https://www.google.com/maps/place/Toftes+Hundesalon/@56.1033594,10.1411375,17z/data=!3m1!4b1!4m5!3m4!1s0x464c6a81cbd22337:0xbf9b78619130234b!8m2!3d56.1033594!4d10.1433262" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-stone-800 text-white px-12 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all inline-flex items-center gap-3"
              >
                <MapPin className="w-5 h-5" /> Se på kort
              </a>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <img src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/a90b200b-b665-4cc2-9ae3-67223f2c9fbb.jpeg" alt="Salonen indgang" className="rounded-none shadow-2xl" />
              <img src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/f4805d42-d514-4e4a-8b83-78a790091ff8.jpg" alt="Salonen interiør" className="rounded-none shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/klipning" className="text-xs uppercase tracking-[0.4em] font-bold text-stone-800 hover:text-stone-600 inline-flex items-center gap-3 transition-all">
            Læs mere om klipning her <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
